import { normalize } from "normalizr";
import React from "react";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";

import { ContestEntity, ContestSchema } from "@/mk2/apps/forum/schemas";
import { Interpolate } from "@/mk2/components/Interpolate";
import { XHRAction, XHRPostError, handleXHRPostErrorSaga, normalizeError } from "@/mk2/helpers/api";
import { showSuccessToast } from "@/mk2/helpers/toasts";
import { getLogger } from "@/mk2/logger";
import {
    WALL_GROWL_SUBSCRIBED_TO_CONTEST_ANNOUNCEMENT,
    WALL_GROWL_UNSUBSCRIBED_FROM_CONTEST_ANNOUNCEMENT,
} from "@/mk/autogenerated/translation";
import { contests_api_home_url, contests_api_set_following_url } from "@/mk/urls/functions";

import {
    CONTESTS_HOME_LOAD,
    CONTESTS_HOME_SET_FOLLOWING,
    ContestsHomeFetchNormalizedResponse,
    ContestsHomeFetchRequestAction,
    ContestsHomeSetFollowingAction,
    contestsHomeFetch,
    contestsHomeSetFollowingApi,
} from "./ContestsHome.actions";

const logger = getLogger("contests/ContestsHome.sagas");

interface ApiResponsePosts {
    body: {
        runningContests: ContestEntity[];
        finishedContests: ContestEntity[];
        draftContests: ContestEntity[];
        hasMore: boolean;
        isFollowing: boolean;
        hasNext: number;
        hasPrevious: number;
    };
}

function* load({ after, xhr }: ContestsHomeFetchRequestAction & XHRAction) {
    yield put(contestsHomeFetch.request(after));

    try {
        const response: ApiResponsePosts = yield call(() =>
            xhr.get(contests_api_home_url(), { after }),
        );

        const normalizedResponse: ContestsHomeFetchNormalizedResponse = normalize(response.body, {
            runningContests: [ContestSchema],
            finishedContests: [ContestSchema],
            draftContests: [ContestSchema],
        });

        yield put(
            contestsHomeFetch.success(
                after,
                response.body.hasMore,
                response.body.isFollowing,
                response.body.hasNext,
                response.body.hasPrevious,
                normalizedResponse,
            ),
        );
    } catch (error) {
        logger.error(error);

        yield put(contestsHomeFetch.failure(after, normalizeError(error)));
    }
}

interface ApiResponseContestsHomeSetFollowing {
    body: {};
}

function* setFollowing({ value, xhr }: ContestsHomeSetFollowingAction & XHRAction) {
    yield put(contestsHomeSetFollowingApi.request(value));

    try {
        const response: ApiResponseContestsHomeSetFollowing = yield call(() =>
            xhr.post(contests_api_set_following_url(), {
                isFollowing: value,
            }),
        );

        const normalizedResponse = normalize(response.body, {});

        yield put(contestsHomeSetFollowingApi.success(value, normalizedResponse));

        yield showSuccessToast(
            <Interpolate
                i18nKey={
                    value
                        ? WALL_GROWL_SUBSCRIBED_TO_CONTEST_ANNOUNCEMENT
                        : WALL_GROWL_UNSUBSCRIBED_FROM_CONTEST_ANNOUNCEMENT
                }
                useDangerouslySetInnerHTML
            />,
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(contestsHomeSetFollowingApi.failure(value, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(CONTESTS_HOME_LOAD, load),
        takeEvery(CONTESTS_HOME_SET_FOLLOWING, setFollowing),
    ]);
}
